import type {
  GenerationProjectFuelGasSupplyDuty,
  GenerationProjectPressureDatum,
} from '../../types/generationImpact';

/**
 * Gas-interconnect pressure derate — screening-level.
 *
 * A meter station's capacity is quoted AT a delivery pressure. Standard-volume
 * throughput through a velocity-limited run scales with absolute pressure, so a
 * station rated 60.6 MMscf/day at 650 psig passes materially less at the
 * transporter's guaranteed minimum. Screening pressure adequacy and volume
 * adequacy independently lets a scenario pass both while being physically
 * unable to flow both at once; this module closes that gap.
 *
 * DERIVATION. At a fixed maximum velocity `v` through a fixed area `A`, and a
 * fixed flowing temperature:
 *
 *   mdot = rho_act * A * v,   rho_act = P_abs * M / (Z * R * T)
 *   Q_std = mdot / rho_std                    =>  Q_std proportional to P_abs / Z
 *
 *   Q_std(P_min) = Q_std(P_rated) * (P_min_abs / P_rated_abs) * (Z_rated / Z_min)
 *
 * The geometry, gas molar mass and flowing temperature all cancel in the ratio,
 * which is why this screen needs no pipe schedule and no meter count.
 *
 * THIS MODULE IS A LEAF. `solveGenerationImpact` imports it, and
 * `generationProjectDuty` already sits downstream of the solver via
 * `generationEquipmentCatalog`; importing anything but types would close a
 * cycle.
 *
 * ARITHMETIC IS RESTRICTED to `+ - * /`, `Math.min` and `Math.abs`. The publish
 * gate re-solves every stored scenario and requires a byte-identical result
 * hash. IEEE-754 basic operations are exactly specified by the standard;
 * `Math.pow` / `exp` / `log` are explicitly NOT correctly-rounded and may
 * differ across JS engines and versions. Do not introduce them here.
 */

/**
 * Used to convert gauge pressures to absolute.
 *
 * Unlike `generationProjectDutyGasPressureCheck`, which REFUSES to compare data
 * on mixed references, this module converts freely. That divergence is
 * deliberate, not an inconsistency: the pressure check compares absolute
 * MAGNITUDES, where a ~101 kPa error is several percent of a typical margin and
 * can flip a verdict. The derate is a RATIO, where the same error is
 * negligible — at 650/500 psig the ratio is 0.774323 at 101.325 kPa and
 * 0.773870 at 95 kPa (roughly 500 m elevation), a difference of 0.04%.
 * Refusing here would discard a useful screen to avoid a rounding error.
 */
export const STANDARD_ATMOSPHERE_KPA = 101.325;

/**
 * One-sided uncertainty applied to the derated capacity when real-gas
 * compressibility was not documented.
 *
 * Z for ~0.6-specific-gravity pipeline gas runs about 0.88-0.93 between 400 and
 * 900 psig at 40-80 degF. Over a 100-200 psi derate span the RATIO Z_rated/Z_min
 * lands in roughly 0.97-1.00, so 3% is a conservative one-sided bound on the
 * ideal-gas (ratio = 1) result, which always over-states deliverability.
 */
export const IDEAL_GAS_DERATE_UNCERTAINTY_FRACTION = 0.03;

export type GasInterconnectDerateStatus =
  | 'derated'
  | 'minimum-pressure-undocumented'
  | 'not-computable';

export type GasInterconnectBindingLimit = 'station-design' | 'contract-mdq';

export interface GasInterconnectDerate {
  status: GasInterconnectDerateStatus;
  /** Station rating expressed in Dth/day at `ratedAtPressure`. */
  stationDthPerDay: number | null;
  /** Contract MDQ in Dth/day, or null when no transportation agreement exists. */
  contractDthPerDay: number | null;
  /** min(station, contract) at the RATED pressure. */
  firmDeliverableDthPerDay: number | null;
  firmBindingLimit: GasInterconnectBindingLimit | null;
  /** Station rating scaled to the documented minimum delivery pressure. */
  deratedStationDthPerDay: number | null;
  /** min(derated station, contract). Null when the minimum pressure is undocumented. */
  deratedDeliverableDthPerDay: number | null;
  deratedBindingLimit: GasInterconnectBindingLimit | null;
  /** P_min_abs / P_rated_abs. */
  absolutePressureRatio: number | null;
  /** Z_rated / Z_min actually applied. Exactly 1 when compressibility is undocumented. */
  compressibilityRatio: number;
  compressibilityBasis: 'documented' | 'ideal-gas-assumed';
  /** One-sided band on `deratedDeliverableDthPerDay`. Exactly 0 when Z is documented. */
  uncertaintyDthPerDay: number;
  /** Every assumption actually applied, for the risk message and the exhibit. */
  assumptions: string[];
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function absoluteKPa(datum: GenerationProjectPressureDatum): number | null {
  if (!Number.isFinite(datum.valueKPa) || datum.valueKPa < 0) return null;
  return datum.reference === 'absolute'
    ? datum.valueKPa
    : datum.valueKPa + STANDARD_ATMOSPHERE_KPA;
}

/**
 * Convert a documented capacity to Dth/day.
 *
 * For the standard-volume basis the megas cancel exactly:
 *   1 MMscf/day * H Btu/scf = 1e6 * H Btu/day = H MMBtu/day = H Dth/day
 * so Dth/day is simply MMscf/day * Btu/scf — no 1e6 factor, no rounding.
 *
 * Returns null when the inputs cannot produce a physical quantity, so callers
 * surface "not computable" rather than propagating NaN into a risk message.
 */
export function gasCapacityDthPerDay(
  basis: GenerationProjectFuelGasSupplyDuty['capacityBasis'],
  value: number,
): number | null {
  if (!isPositiveFinite(value)) return null;
  if (basis.kind === 'energy') return value;
  if (!isPositiveFinite(basis.documentedHhvBtuPerScf)) return null;
  return value * basis.documentedHhvBtuPerScf;
}

/**
 * Firm deliverable at the RATED pressure — the number the dossier applies to
 * `availableGasCapacityDthPerDay`, and only when
 * `quantityBasis === 'firm-transport-agreement'`.
 *
 * This is deliberately the RATED, not the derated, figure. Writing the derated
 * number would fold the pressure finding into `gas-capacity-mismatch` and make
 * the two risks double-count the same defect under different ids. Keeping them
 * separate screens two genuinely different bounds: contractual/nameplate versus
 * pressure-derated.
 */
export function firmGasDeliverableDthPerDay(
  supply: GenerationProjectFuelGasSupplyDuty,
): number | null {
  const station = gasCapacityDthPerDay(supply.capacityBasis, supply.stationDesignCapacity);
  if (station == null) return null;
  if (supply.contractMaxDailyQuantity == null) return station;
  const contract = gasCapacityDthPerDay(
    supply.capacityBasis,
    supply.contractMaxDailyQuantity,
  );
  if (contract == null) return null;
  return Math.min(station, contract);
}

const NOT_COMPUTABLE: GasInterconnectDerate = {
  status: 'not-computable',
  stationDthPerDay: null,
  contractDthPerDay: null,
  firmDeliverableDthPerDay: null,
  firmBindingLimit: null,
  deratedStationDthPerDay: null,
  deratedDeliverableDthPerDay: null,
  deratedBindingLimit: null,
  absolutePressureRatio: null,
  compressibilityRatio: 1,
  compressibilityBasis: 'ideal-gas-assumed',
  uncertaintyDthPerDay: 0,
  assumptions: [],
};

/**
 * Screen the documented interconnect against the transporter's guaranteed
 * minimum delivery pressure.
 *
 * `documentedSiteSupplyMinimum` is deliberately the SAME field the static
 * pressure check consumes — it is the transporter's guaranteed minimum at the
 * custody point, and a second copy of that number would let the two screens
 * disagree with neither authoritative.
 */
export function deriveGasInterconnectDerate(
  supply: GenerationProjectFuelGasSupplyDuty,
  documentedSiteSupplyMinimum: GenerationProjectPressureDatum | undefined,
): GasInterconnectDerate {
  const station = gasCapacityDthPerDay(supply.capacityBasis, supply.stationDesignCapacity);
  if (station == null) return { ...NOT_COMPUTABLE, assumptions: [] };

  const contract = supply.contractMaxDailyQuantity == null
    ? null
    : gasCapacityDthPerDay(supply.capacityBasis, supply.contractMaxDailyQuantity);
  if (supply.contractMaxDailyQuantity != null && contract == null) {
    return { ...NOT_COMPUTABLE };
  }

  const firmDeliverable = contract == null ? station : Math.min(station, contract);
  const firmBindingLimit: GasInterconnectBindingLimit =
    contract != null && contract < station ? 'contract-mdq' : 'station-design';

  const assumptions: string[] = [];
  if (supply.capacityBasis.kind === 'standard-volume') {
    assumptions.push(
      `Volumetric capacity converted at a documented ${supply.capacityBasis.documentedHhvBtuPerScf} Btu/scf HHV`
      + ` (base ${supply.capacityBasis.standardConditions.basePressureKPaAbsolute} kPa absolute,`
      + ` ${supply.capacityBasis.standardConditions.baseTemperatureC} degC).`,
    );
  }
  if (supply.quantityBasis === 'indicative') {
    assumptions.push(
      'Quantities are indicative (scoping / ROM class), not firm transport;'
      + ' firm capacity still requires an executed transportation agreement.',
    );
  }

  const ratedAbs = absoluteKPa(supply.ratedAtPressure);
  const minimumAbs = documentedSiteSupplyMinimum == null
    ? null
    : absoluteKPa(documentedSiteSupplyMinimum);

  if (ratedAbs == null || !isPositiveFinite(ratedAbs)) return { ...NOT_COMPUTABLE, assumptions };

  if (minimumAbs == null) {
    return {
      status: 'minimum-pressure-undocumented',
      stationDthPerDay: station,
      contractDthPerDay: contract,
      firmDeliverableDthPerDay: firmDeliverable,
      firmBindingLimit,
      deratedStationDthPerDay: null,
      deratedDeliverableDthPerDay: null,
      deratedBindingLimit: null,
      absolutePressureRatio: null,
      compressibilityRatio: 1,
      compressibilityBasis: supply.compressibility ? 'documented' : 'ideal-gas-assumed',
      uncertaintyDthPerDay: 0,
      assumptions,
    };
  }

  const zRated = supply.compressibility?.atRatedPressure;
  const zMinimum = supply.compressibility?.atMinimumDeliveryPressure;
  const compressibilityDocumented = isPositiveFinite(zRated) && isPositiveFinite(zMinimum);
  const compressibilityRatio = compressibilityDocumented ? zRated / zMinimum : 1;

  assumptions.push(
    supply.ratedAtPressure.reference === 'gauge'
      || documentedSiteSupplyMinimum?.reference === 'gauge'
      ? `Gauge pressures converted to absolute at ${STANDARD_ATMOSPHERE_KPA} kPa; the derate is a ratio, so barometric variation moves it well under 0.1%.`
      : 'Pressures compared on an absolute basis as documented.',
  );
  assumptions.push(
    compressibilityDocumented
      ? `Real-gas compressibility applied (Z ${zRated} rated, ${zMinimum} at minimum delivery pressure).`
      : 'Compressibility not documented; screened on an ideal-gas basis, which OVER-states deliverability.'
        + ` A ${IDEAL_GAS_DERATE_UNCERTAINTY_FRACTION * 100}% one-sided band is carried on the derated capacity.`,
  );
  // A station the user has declared ALREADY DERATED is taken as given: the
  // absolute-pressure model belongs to a velocity-limited meter run, and
  // applying it to a regulation- or compression-limited station would be the
  // wrong physics. The platform does not substitute a derate model it has no
  // documented basis for — it applies none and says so.
  const alreadyDerated = supply.capacityLimit === 'documented-derated-at-site-minimum';
  assumptions.push(
    alreadyDerated
      ? 'Station capacity documented as already derated to the site minimum delivery pressure;'
        + ' no absolute-pressure scaling applied. The quoted figure is taken as the site-deliverable rate.'
      : 'Velocity-limited meter run assumed; a regulation- or compression-limited station does not follow'
        + ' absolute-pressure scaling and must be documented already derated.',
  );

  const absolutePressureRatio = alreadyDerated ? 1 : minimumAbs / ratedAbs;
  const deratedStation = station * absolutePressureRatio * compressibilityRatio;
  const deratedDeliverable = contract == null
    ? deratedStation
    : Math.min(deratedStation, contract);
  const deratedBindingLimit: GasInterconnectBindingLimit =
    contract != null && contract < deratedStation ? 'contract-mdq' : 'station-design';

  return {
    status: 'derated',
    stationDthPerDay: station,
    contractDthPerDay: contract,
    firmDeliverableDthPerDay: firmDeliverable,
    firmBindingLimit,
    deratedStationDthPerDay: deratedStation,
    deratedDeliverableDthPerDay: deratedDeliverable,
    deratedBindingLimit,
    absolutePressureRatio,
    compressibilityRatio,
    compressibilityBasis: compressibilityDocumented ? 'documented' : 'ideal-gas-assumed',
    uncertaintyDthPerDay: compressibilityDocumented
      ? 0
      : deratedDeliverable * IDEAL_GAS_DERATE_UNCERTAINTY_FRACTION,
    assumptions,
  };
}
